/**
 * The next step for the claim the reader is holding (D349): at most one press,
 * named for what it does, and the record that put it there — shown above the
 * composer, where every press against a node now lives (D347).
 *
 * A RULE, NOT A RECOMMENDATION. Each step is a predicate over stored records,
 * tried in one written order, first match wins — the coach's contract
 * (`core/cues.ts`, invariant 8) at the scale of one claim. None reads the model's
 * own ratings or its prose, so no step can be the model steering the reader
 * toward its own work, and each carries its WHY line in the words the card's own
 * status line uses (`stateClauses`), so the two cannot disagree about what is
 * missing. Where no rule fires there is no step: a filler press on every claim
 * would teach the reader to stop reading this line.
 *
 *   compare   An objection and the claim it hangs off, both open, never compared:
 *             "Why do these disagree?", the Lab's structured comparison
 *             (`core/disagreement.ts`). Two readings of one thing, and one of
 *             them has to go, so it comes first.
 *   survey    The question, its field not surveyed yet.
 *   priorArt  An insight nothing has searched for prior art yet (D288).
 *   check     A claim not checked yet against sources — the question every card
 *             is read for (D143, D157).
 *
 * Nothing on a closed path, on a claim that carries a recorded result (it
 * outranks everything the model said, and a step would say otherwise), or on an
 * objection whose comparison has been made and whose own reading is in.
 *
 * DATA, NEVER A CLOSURE (invariant 9). A step names its press; the composer
 * dispatches it through the same doors as the modes' buttons (`pressVerb`,
 * `pressDisagree`), with the same price, the same refusals and the same Up next.
 * Nothing here calls a model or writes state. Pure over a `Session`.
 */

import { comparisonsFor, disagreePairOf } from './disagreement'
import { stateClauses, type StateClause } from './stateClauses'
import type { ReasonNode, Session } from './types'
import { CONJECTURE_LINE_WORD, upNextName } from './upNext'
import { STATUS_WORD } from './vocabulary'

export type NextStepKind = 'compare' | 'survey' | 'priorArt' | 'check'

export interface NextStep {
  kind: NextStepKind
  /** The node the press acts on: the selection — or, for a comparison opened
   *  from the claim's side, the objection, whose pair the press compares. */
  targetId: string
  /** The press's own name, as the modes and the card print it. */
  title: string
  /** The WHY line: what on record put this step here. */
  reason: string
}

/** The comparison's own words (`NodeCard`'s press, `ui/disagreePress.ts`). */
export const COMPARE_TITLE = 'Why do these disagree?'

/** "Conflicts with “…”": the other side of the open pair, by its label. */
export const conflictsWith = (label: string): string => `Conflicts with “${label}”`

/** "N claims rest on it": the count under a claim, when there is one. */
export const restOnIt = (n: number): string =>
  `${n} ${n === 1 ? 'claim rests' : 'claims rest'} on it`

/** A clause, as a line that OPENS with it reads: the table's capital restored. */
const opening = (c: StateClause): string => c.word.charAt(0).toUpperCase() + c.word.slice(1)

/** An objection still in play: not closed, no result recorded on it. */
const openObjection = (n: ReasonNode): boolean =>
  n.kind === 'contradiction' && !n.terminal && !n.outcome

function childrenOf(session: Pick<Session, 'nodes' | 'order'>, id: string): ReasonNode[] {
  const out: ReasonNode[] = []
  for (const cid of session.order) {
    const c = session.nodes[cid]
    if (c && c.parentId === id) out.push(c)
  }
  return out
}

/** Every claim under this one, however deep — what moves if it moves. */
function downstream(session: Pick<Session, 'nodes' | 'order'>, id: string): number {
  const kids = new Map<string, string[]>()
  for (const cid of session.order) {
    const p = session.nodes[cid]?.parentId
    if (!p) continue
    const list = kids.get(p)
    if (list) list.push(cid)
    else kids.set(p, [cid])
  }
  let n = 0
  const stack = [...(kids.get(id) ?? [])]
  const seen = new Set<string>()
  while (stack.length) {
    const c = stack.pop()!
    if (seen.has(c)) continue
    seen.add(c)
    n++
    stack.push(...(kids.get(c) ?? []))
  }
  return n
}

/**
 * The open pair this claim is in, with the objection the press runs on and the
 * label of the other side — or null. From the objection's own side the pair is
 * the one its card's press compares (`disagreePairOf`); from the claim's side it
 * is the first open objection hanging off it, in the order they landed.
 */
function openPair(
  session: Session,
  node: ReasonNode,
): { objectionId: string; leftId: string; rightId: string; other: string } | null {
  if (openObjection(node)) {
    const pair = disagreePairOf(session, node)
    const parent = pair ? session.nodes[pair.leftId] : undefined
    if (!pair || !parent || parent.terminal || parent.outcome) return null
    return { objectionId: node.id, ...pair, other: parent.label }
  }
  if (node.parentId === null || node.terminal) return null
  const objection = childrenOf(session, node.id).find(openObjection)
  if (!objection) return null
  return {
    objectionId: objection.id,
    leftId: node.id,
    rightId: objection.id,
    other: objection.label,
  }
}

/** Whether the Lab has compared exactly these two already — the card shows that. */
function compared(session: Session, objectionId: string, leftId: string, rightId: string): boolean {
  return comparisonsFor(session, objectionId).some((a) =>
    [a.left.nodeId, a.right.nodeId].every((id) => id === leftId || id === rightId),
  )
}

/** The one step for this claim, or null where no rule fires. */
export function nextStepFor(session: Session, nodeId: string): NextStep | null {
  const node = session.nodes[nodeId]
  if (!node || node.terminal || node.outcome) return null

  const pair = openPair(session, node)
  if (pair && !compared(session, pair.objectionId, pair.leftId, pair.rightId)) {
    return {
      kind: 'compare',
      targetId: pair.objectionId,
      title: COMPARE_TITLE,
      reason: conflictsWith(pair.other),
    }
  }

  const clauses = stateClauses(node)
  const isRoot = node.parentId === null
  const survey = clauses.find((c) => c.key === 'survey')
  if (isRoot) {
    return survey && survey.word === STATUS_WORD.fieldNotSurveyed.toLowerCase()
      ? {
          kind: 'survey',
          targetId: node.id,
          title: upNextName('verify', true),
          reason: opening(survey),
        }
      : null
  }

  // An insight's line says whether its prior-art search has run, never "not
  // checked yet" (`priorArtWord`): its first press is the search (D288).
  const searched = clauses.find((c) => c.key === 'prior-art')
  if (searched) {
    return searched.word === STATUS_WORD.notSearchedForPriorArt.toLowerCase()
      ? {
          kind: 'priorArt',
          targetId: node.id,
          title: CONJECTURE_LINE_WORD.priorArt,
          reason: opening(searched),
        }
      : null
  }

  const unchecked = clauses.find((c) => c.key === 'unchecked')
  if (unchecked) {
    const under = downstream(session, node.id)
    return {
      kind: 'check',
      targetId: node.id,
      title: upNextName('verify', false),
      reason: under > 0 ? `${opening(unchecked)} · ${restOnIt(under)}` : opening(unchecked),
    }
  }
  return null
}
